import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Switch,
  FormControlLabel,
  Divider
} from '@mui/material';
import { collection, doc, getDoc, setDoc, db } from '../../firebase/firebase';

const SettingsManagement = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [settings, setSettings] = useState({
    siteName: '',
    siteDescription: '',
    contactEmail: '',
    contactPhone: '',
    address: '',
    maintenanceMode: false,
    allowRegistration: true,
    allowGuestCheckout: false,
    paymentMethods: {
      paypal: true,
      stripe: false,
      wechat: false,
      alipay: false
    },
    socialMedia: {
      facebook: '',
      twitter: '',
      instagram: '',
      youtube: ''
    }
  });

  // 加载设置
  useEffect(() => {
    fetchSettings();
  }, []);

  // 获取设置
  const fetchSettings = async () => {
    try {
      setLoading(true);
      const settingsDoc = await getDoc(doc(db, 'settings', 'general'));
      if (settingsDoc.exists()) {
        setSettings(settingsDoc.data());
      }
    } catch (err) {
      console.error('获取设置失败:', err);
      setError('获取设置失败');
    } finally {
      setLoading(false);
    }
  };

  // 处理输入变化
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 处理开关变化
  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  // 处理支付方式变化
  const handlePaymentMethodChange = (method) => {
    setSettings(prev => ({
      ...prev,
      paymentMethods: {
        ...prev.paymentMethods,
        [method]: !prev.paymentMethods[method]
      }
    }));
  };

  // 处理社交媒体链接变化
  const handleSocialMediaChange = (platform, value) => {
    setSettings(prev => ({
      ...prev,
      socialMedia: {
        ...prev.socialMedia,
        [platform]: value
      }
    }));
  };

  // 保存设置
  const handleSave = async () => {
    try {
      setSaving(true);
      await setDoc(doc(db, 'settings', 'general'), {
        ...settings,
        updatedAt: new Date()
      });
      setError(null);
    } catch (err) {
      console.error('保存设置失败:', err);
      setError('保存设置失败');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>基本设置</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="网站名称"
              name="siteName"
              value={settings.siteName}
              onChange={handleInputChange}
              margin="normal"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="网站描述"
              name="siteDescription"
              value={settings.siteDescription}
              onChange={handleInputChange}
              margin="normal"
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="联系邮箱"
              name="contactEmail"
              value={settings.contactEmail}
              onChange={handleInputChange}
              margin="normal"
              type="email"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="联系电话"
              name="contactPhone"
              value={settings.contactPhone}
              onChange={handleInputChange}
              margin="normal"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="地址"
              name="address"
              value={settings.address}
              onChange={handleInputChange}
              margin="normal"
              multiline
              rows={2}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>系统设置</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.maintenanceMode}
                  onChange={handleSwitchChange}
                  name="maintenanceMode"
                />
              }
              label="维护模式"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.allowRegistration}
                  onChange={handleSwitchChange}
                  name="allowRegistration"
                />
              }
              label="允许注册"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.allowGuestCheckout}
                  onChange={handleSwitchChange}
                  name="allowGuestCheckout"
                />
              }
              label="允许游客结账"
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>支付设置</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.paymentMethods.paypal}
                  onChange={() => handlePaymentMethodChange('paypal')}
                />
              }
              label="PayPal"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.paymentMethods.stripe}
                  onChange={() => handlePaymentMethodChange('stripe')}
                />
              }
              label="Stripe"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.paymentMethods.wechat}
                  onChange={() => handlePaymentMethodChange('wechat')}
                />
              }
              label="微信支付"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.paymentMethods.alipay}
                  onChange={() => handlePaymentMethodChange('alipay')}
                />
              }
              label="支付宝"
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>社交媒体设置</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Facebook"
              value={settings.socialMedia.facebook}
              onChange={(e) => handleSocialMediaChange('facebook', e.target.value)}
              margin="normal"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Twitter"
              value={settings.socialMedia.twitter}
              onChange={(e) => handleSocialMediaChange('twitter', e.target.value)}
              margin="normal"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Instagram"
              value={settings.socialMedia.instagram}
              onChange={(e) => handleSocialMediaChange('instagram', e.target.value)}
              margin="normal"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="YouTube"
              value={settings.socialMedia.youtube}
              onChange={(e) => handleSocialMediaChange('youtube', e.target.value)}
              margin="normal"
            />
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? '保存中...' : '保存设置'}
        </Button>
      </Box>
    </Box>
  );
};

export default SettingsManagement; 